import { useEffect, useRef, useState } from 'react';

/**
 * Hold a transient `true` for at least `ms` once it appears.
 *
 * A search that resolves from the RTK cache can finish inside a frame, which would
 * strobe the loading bar rather than communicate anything. This puts a floor under
 * how long the bar is on screen, so a fast response reads as fast rather than as a
 * flicker.
 *
 * Deliberately JS and not a CSS transition delay: CSS can postpone the *start* of an
 * animation, but the element unmounts the moment `active` goes false, so a delay can
 * only hide a fast state — it cannot make one linger. Only a timer can enforce a
 * minimum.
 *
 * Rising edges are immediate; only the fall is delayed. A second rise while the
 * floor is still running restarts it from the new edge.
 */
export const useMinimumDuration = (active: boolean, ms: number): boolean => {
  const [held, setHeld] = useState(active);
  // When the current `true` streak began, so the remaining floor can be measured
  // from the rising edge rather than from whenever this effect happens to run.
  const shownAtRef = useRef<number | null>(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      // Only stamp a genuine rising edge; re-renders while already active must not
      // push the deadline forward, or a busy page could hold the bar indefinitely.
      if (shownAtRef.current === null) shownAtRef.current = Date.now();
      setHeld(true);
      return;
    }

    if (shownAtRef.current === null) {
      setHeld(false);
      return;
    }

    const remaining = ms - (Date.now() - shownAtRef.current);
    if (remaining <= 0) {
      shownAtRef.current = null;
      setHeld(false);
      return;
    }

    const timer = setTimeout(() => {
      shownAtRef.current = null;
      setHeld(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [active, ms]);

  return held;
};
